import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useApi } from '../../../app/providers';
import { href, navigate } from '../../../app/router';
import { useTheme } from '../../../app/theme-store';
import type { Locale } from '../../../lib/api';
import { avatarPhoto } from '../../../lib/avatar';
import { Avatar, Menu } from '../../../ui';
import { cn } from '../../../ui/cn';
import { focusRing } from '../../../ui/focus';
import { Icon } from '../../../ui/icons';
import { AboutDialog } from '../../about/AboutDialog';
import { CLIENT_BUILD } from '../../about/version';
import { qk } from '../queries';

const THEMES = [
  ['system', 'dashboard.account.themeSystem'],
  ['light', 'dashboard.account.themeLight'],
  ['dark', 'dashboard.account.themeDark'],
] as const;
const LANGUAGES: readonly [Locale, string][] = [
  ['fr', 'Français'],
  ['en', 'English'],
];

/** Account at the foot of the sidebar (P16-4 Ruling 7): profile, theme, language, sign out, and the version (P5g). */
export function AccountMenu({ collapsed }: { collapsed: boolean }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const [theme, setTheme] = useTheme();
  const profile = useQuery({ queryKey: qk.profile, queryFn: () => api.getProfile() });
  const [about, setAbout] = useState(false);
  const name = profile.data?.displayName || t('dashboard.account.label');
  const check = (on: boolean) => (
    <Icon icon={Check} size={14} className={on ? undefined : 'invisible'} />
  );
  const language = async (locale: Locale) => {
    await i18n.changeLanguage(locale);
    await api.updateProfile({ locale }).catch(() => {});
  };
  return (
    <>
      <Menu.Root>
        <Menu.Trigger
          aria-label={t('dashboard.account.label')}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-md p-1.5 text-left font-sans text-sm text-fg hover:bg-surface-2 pointer-coarse:min-h-11',
            focusRing,
          )}
        >
          <Avatar
            name={name}
            color={profile.data?.color ?? '#71717a'}
            photo={profile.data && avatarPhoto(profile.data.avatarKey, profile.data.id)}
            size="sm"
          />
          {!collapsed && (
            <span className="min-w-0 truncate" title={name}>
              {name}
            </span>
          )}
        </Menu.Trigger>
        <Menu.Popup label={t('dashboard.account.label')} side="top">
          <Menu.Item onClick={() => navigate(href('settings'))}>
            {t('dashboard.account.profile')}
          </Menu.Item>
          <Menu.Separator />
          {THEMES.map(([value, key]) => (
            <Menu.Item key={value} onClick={() => setTheme(value)}>
              {check(theme === value)}
              {t(key)}
            </Menu.Item>
          ))}
          <Menu.Separator />
          {LANGUAGES.map(([value, label]) => (
            <Menu.Item key={value} lang={value} onClick={() => void language(value)}>
              {check(i18n.language.startsWith(value))}
              {label}
            </Menu.Item>
          ))}
          <Menu.Separator />
          <Menu.Item onClick={() => void api.auth.signOut()}>{t('auth.signOut')}</Menu.Item>
          <Menu.Separator />
          <Menu.Item onClick={() => setAbout(true)}>
            {t('dashboard.account.about')}
            <span className="text-fg-muted">· v{CLIENT_BUILD.version}</span>
          </Menu.Item>
        </Menu.Popup>
      </Menu.Root>
      {about && <AboutDialog onClose={() => setAbout(false)} />}
    </>
  );
}
